/**
 * Persistence. One slot, stored in localStorage, written when the player docks
 * at a station and on nothing else — a save that happens mid-fight would let a
 * bad situation be saved into, and a save that happens on quit would be lost by
 * a phone that gets backgrounded and killed.
 */

import { emptyLevels, type Levels } from './upgrades';
import { DEFAULT_SUN } from './config';

/**
 * ONE SLOT PER MODE, not one slot.
 *
 * There was a single key, and the save it held carried no record of which mode
 * had written it — so CONTINUE resumed the stored run into whichever mode
 * happened to be loaded. Leave a descent, start an expedition, and the descent
 * was unreachable: there was no door back to it and nothing on any screen said
 * one had been closed.
 *
 * The mode is part of what a run IS, so it is part of the save, and the key is
 * derived from it. Survival never writes one — it does not persist by design.
 */
const KEY = 'abyss.save.v2';
const keyFor = (mode: string) => `${KEY}.${mode}`;
/** Modes that keep a save. Survival is deliberately absent. */
export const SAVED_MODES = ['campaign', 'expedition'] as const;
/**
 * Exported so `World.toSave` can stamp it explicitly rather than inheriting it
 * from a `blankSave()` spread. Do not bump this without writing a migrator:
 * `loadSave` returns null on a mismatch and the key itself carries the version,
 * so a bump deletes every player's progress with no error — it surfaces only as
 * a missing CONTINUE row on the title.
 */
export const SAVE_VERSION = 2;

export interface SaveData {
  v: number;
  /**
   * Which mode wrote this. New in version 2 without a bump — a save from
   * before this field existed reads back as 'campaign', which is where every
   * such save came from, because it is the only mode that had been shipped.
   */
  mode: string;
  /** Station id to respawn at. Empty means the world spawn. */
  station: string;
  /**
   * Where the sub actually was when the dive was left, so CONTINUE puts it
   * back there rather than at the last station.
   *
   * A checkpoint is where you go when you DIE — a cost, and a promise that a
   * bad minute cannot take an hour. Leaving the game is not a death, and being
   * teleported backwards for closing the tab is a punishment for stopping.
   * Death still returns to `station`; only resuming reads this.
   *
   * Optional because older saves do not have it, and a save without it must
   * still resume at its station rather than at the world origin.
   */
  at?: { x: number; y: number; lane?: number };
  levels: Levels;
  salvage: number;
  /**
   * Shards in the hold, one count per seam, indexed by `ORE`.
   *
   * Optional because every save written before the hold existed has none, and
   * an absent manifest is an empty one rather than a broken file.
   */
  ore?: number[];
  /** Deposits already broken, by site key. See `World.oreKey`. */
  minedOre?: string[];
  /**
   * Shards already taken from seams that are still standing.
   *
   * The reserve is the point of a hold capacity: you leave with a seam half
   * worked and it has to be half worked when you come back.
   */
  oreProgress?: [string, number][];
  score: number;
  lives: number;
  torpedoes: number;
  charges: number;
  kills: number;
  /** Ids of everything already found, so the world does not re-award it. */
  seenPois: string[];
  stations: string[];
  bosses: string[];
  takenPickups: string[];
  /**
   * Authored enemies already destroyed, by `Enemy.zoneKey`.
   *
   * `respawn` has always kept everything dead within a session — "a hard stretch
   * gets a little easier each attempt" — and the save recorded defeated BOSSES
   * and taken pickups and nothing else, so CONTINUE quietly repopulated every
   * minefield the player had cleared. Same field, same positions, because the
   * spawn walk is deterministic. What you cleared stays cleared now, across a
   * reload as well as across a death.
   */
  clearedEnemies: string[];
  /** Onboarding lines already shown. */
  hints: string[];
  /** Craters the player has blown, flattened as [x, y, r, ...]. */
  craters: number[];
  /** Seconds of play, for the summary screen. */
  playTime: number;
  /** Furthest x reached, for the map strip. */
  reachedX: number;
  /**
   * Whether the finale has been reached. New in version 2 without a version
   * bump: `loadSave` merges over `blankSave()`, so a save written before this
   * field existed reads back as `false`, which is the right answer for it.
   */
  victory: boolean;
  /**
   * Where cargo has got to. Only crates that have actually moved are listed —
   * anything still sitting where it was authored is already in `level.ts` and
   * does not need saying twice.
   *
   * Without this, hauling a container two zones and then docking would put it
   * straight back where it started, which is the single most infuriating thing
   * a save could do to somebody who just spent four minutes towing it.
   */
  cargo: { id: string; x: number; y: number; lane?: number }[];
  /** Id of the crate on the line when the game was saved, or ''. */
  towing: string;

  /**
   * The fleet. Structural modules — hull, shield, engine — belong to the boat
   * they are fitted to, because they ARE the boat.
   *
   * New in version 2 without a bump. A save written before this existed has an
   * empty fleet and a populated `levels`, which `World.applySave` migrates by
   * splitting the old seven into structure and kit. `levels` is still written
   * for exactly that reason: an older build must be able to read this back.
   */
  fleet: { id: string; levels: Levels }[];
  /**
   * Owned equipment — cannon, torpedo, sonar, lamp. These refit across the
   * whole fleet, so buying a second boat never means re-earning your gun.
   */
  equipment: Levels;
  /** Chassis id currently in the water. */
  active: string;
  /** Chassis ids owned. The starting hull is always in here. */
  hulls: string[];
}

/**
 * Modules bolted to the BOAT, and therefore owned per hull.
 *
 * ONE LIST, because there used to be two and they disagreed. `levelsFor` read
 * nitro, ablative and warding off the hull's own ledger while `ledger` wrote
 * them into the shared kit locker — so buying the burner, the ablative plate or
 * the warding field debited the salvage, incremented a number nothing ever
 * read, and left the boat exactly as it was. The max-level guard reads the same
 * side, so they could also be bought again, and again, forever.
 *
 * Both now derive from here, which is the only way they cannot drift apart
 * again. Anything not in this list is kit: a gun is a gun on whichever boat is
 * carrying it.
 */
export const HULL_MODULES = [
  'hull', 'shield', 'engine', 'nitro', 'ablative', 'warding', 'winch',
] as const;

export function isHullModule(id: string): boolean {
  return (HULL_MODULES as readonly string[]).includes(id);
}

export function blankSave(): SaveData {
  return {
    v: SAVE_VERSION,
    mode: 'campaign',
    station: '',
    levels: emptyLevels(),
    salvage: 0,
    ore: [],
    minedOre: [],
    oreProgress: [],
    score: 0,
    lives: 3,
    torpedoes: 8,
    charges: 0,
    kills: 0,
    seenPois: [],
    stations: [],
    bosses: [],
    takenPickups: [],
    clearedEnemies: [],
    hints: [],
    craters: [],
    playTime: 0,
    reachedX: 0,
    victory: false,
    cargo: [],
    towing: '',
    fleet: [],
    equipment: emptyLevels(),
    active: 'pathfinder',
    hulls: ['pathfinder'],
  };
}

function storage(): Storage | null {
  try {
    // Safari in private mode throws on access, not on use.
    const s = window.localStorage;
    s.getItem(KEY);
    return s;
  } catch {
    return null;
  }
}

/** The stored save for a mode, or null when there is nothing to continue. */
export function loadSave(mode = 'campaign'): SaveData | null {
  const s = storage();
  if (!s) return null;
  // The old single-key save is adopted by whichever mode it says it was, and
  // by the campaign if it says nothing — which is the honest reading, since
  // that key predates there being anything else to be.
  const raw = s.getItem(keyFor(mode))
    ?? (mode === 'campaign' ? s.getItem(KEY) : null);
  if (!raw) return null;
  try {
    const data = JSON.parse(raw) as Partial<SaveData>;
    if (data.v !== SAVE_VERSION) return null;
    // Merge over a blank so a save written by an older build of the same
    // version still has every field the game reads.
    const out = { ...blankSave(), ...data, mode };
    out.levels = { ...emptyLevels(), ...(data.levels ?? {}) };
    return out;
  } catch {
    return null;
  }
}

export function writeSave(data: SaveData) {
  const s = storage();
  if (!s) return;
  try {
    s.setItem(keyFor(data.mode), JSON.stringify(data));
  } catch {
    // Quota or private mode. A game that cannot save is still a game.
  }
}

export function clearSave(mode?: string) {
  const s = storage();
  if (!s) return;
  if (mode) { s.removeItem(keyFor(mode)); if (mode === 'campaign') s.removeItem(KEY); return; }
  for (const m of SAVED_MODES) s.removeItem(keyFor(m));
  s.removeItem(KEY);
}

export function hasSave(mode = 'campaign'): boolean {
  return loadSave(mode) !== null;
}

/** Every mode with a run waiting, in the order the title should offer them. */
export function savedModes(): string[] {
  return SAVED_MODES.filter((m) => hasSave(m));
}

// ---------------------------------------------------------------------------
// Settings — separate from the run so wiping a save does not mute the game
// ---------------------------------------------------------------------------

export type TierPref = 'auto' | 'mobile' | 'balanced' | 'high';

/**
 * How edges get smoothed.
 *
 * 'msaa' resolves four samples per pixel in the scene pass — the best edges,
 * and the only one that also cleans up the inside of a triangle's silhouette,
 * but it multiplies the cost of the pass this renderer spends most of its
 * frame in. 'fxaa' rasterises once and finds the edges afterwards from the
 * finished image: one cheap full-resolution pass instead of four times the
 * scene, softer on very thin geometry, and most of the quality for a fraction
 * of the cost. 'taa' jitters the projection by a fraction of a pixel each
 * frame and adds the frames up, so the samples arrive over time instead of all
 * at once — the cheapest way to get many of them, at the price of needing to
 * know where every pixel was last frame. 'off' is none of them.
 */
export type AAPref = 'auto' | 'off' | 'fxaa' | 'msaa' | 'taa';

export interface Settings {
  muted: boolean;
  /** Master gain, 0..1. */
  volume: number;
  /**
   * Per-bus levels, 0..1. Three because they are three different jobs: the
   * score is something you might want off entirely while still hearing the
   * game, effects are the game telling you what just happened, and the water
   * hum is atmosphere that some people want and some find fatiguing.
   */
  musicVol: number;
  sfxVol: number;
  ambVol: number;
  showKeymap: boolean;
  /**
   * Frames per second ceiling; 0 lets the display set the rate.
   *
   * Defaults to 60 rather than uncapped. On a 120 Hz laptop the loop will
   * happily draw 120 frames a second of a renderer that is fill-bound by
   * design, which pins the GPU for no gain a player can see — and on a phone
   * the same thing runs the die hot until it throttles, which arrives as a
   * frame rate that gets worse the longer you play.
   */
  frameCap: number;
  /** Quality tier override, or 'auto' to take the device's word for it. */
  tier: TierPref;
  /**
   * Which submarine mesh to fly.
   *
   * 'mk2' is the Fusion 360 rebuild and the default: eleven rigged parts with
   * baked per-vertex occlusion. 'model' is the Tripo scan it replaced, one mesh
   * plus a searchlight. 'classic' is `buildSub()`, the procedural boat the game
   * shipped with.
   *
   * ALL THREE ARE KEPT. They share an origin, an axis, a length and the same
   * MAT slots, so switching changes the silhouette and nothing else — no
   * hardpoint, light or collision radius moves. Two of them being superseded is
   * the point: a hull you cannot switch back to is a hull you cannot judge.
   */
  hull: 'mk2' | 'model' | 'classic';
  /**
   * Depth of field, as an f-number. 0 is off, which is the default: it is a
   * look rather than a fix, it costs a half-resolution gather, and a player who
   * has just turned the detail tier down to keep the frame rate should not have
   * it handed back to them.
   */
  dof: number;
  /**
   * Revision of this settings file, for one-shot migrations of a DEFAULT that
   * changed. Absent on every file written before it existed, which reads as 1.
   *
   * A plain `if (saved.x === old) saved.x = new` cannot do this job: it fires
   * on every load, so the old value stops being SELECTABLE. This is the field
   * that lets a migration happen once and then get out of the way.
   */
  sv?: number;
  /**
   * RENDER SCALE, as a multiplier on the quality tier's pixel budget. 0 means
   * no cap at all — render at the display's own resolution.
   *
   * The tier caps the frame at 2.4 megapixels on `high`. That is a sensible
   * guard for a phone and a severe one for a desktop: an 1800 x 1042 screen at
   * DPR 2 wants 7.5 megapixels, so the scene renders at 0.57 of the linear
   * resolution and is upscaled. Most of the frame tolerates that. Mirrors do
   * not — a reflection carries the highest angular frequency in the scene, and
   * below native it aliases into hard blocks that crawl as the camera moves.
   *
   * Takes effect immediately: `configure()` re-reads the budget on every
   * resize, so cycling this calls `setPixelBudget` and resizes rather than
   * asking for a restart the way TIER and AA do.
   */
  gfxRes: number;
  /**
   * ANISOTROPIC FILTERING, as the max sample count itself: 1, 2, 4, 8 or 16.
   *
   * Stored as the number rather than a 0..4 level because it IS a number the
   * hardware takes, and every other spelling would need a table in both
   * directions. 1 is plain trilinear.
   *
   * It buys exactly one thing, and this game is the case that wants it: a
   * texture on a surface running away from the camera is minified far harder
   * along the direction of travel than across it, and an isotropic sampler has
   * to pick one mip for both — so it takes the blurrier one and the seabed
   * turns to mush a few metres out. Anisotropy takes several samples along the
   * long axis instead. The camera here sits low and looks along the floor, so
   * nearly every terrain pixel is a grazing one.
   *
   * Only the detail array is sampled this way. The environment probe, the post
   * chain, the fluid targets and the font atlas are all either full-screen at
   * 1:1 or reflection lookups, where anisotropy costs samples and changes
   * nothing.
   */
  gfxAf: number;
  /** Antialiasing, or 'auto' to take the tier's word for it. */
  aa: AAPref;
  /**
   * Screen-space effects: ambient occlusion, shadows, reflections, refraction.
   *
   * One switch rather than four. They share a depth march and a G-buffer, they
   * cost roughly the same, and a player who wants the frame rate back wants all
   * of them gone — four separate lines would be four decisions to make about
   * things whose names do not tell you what they look like.
   */
  ssfx: boolean;
  /**
   * Light the hull from the baked underwater capture rather than the analytic
   * studio. On by default: it is the better environment and it costs one
   * texture sample. Off is the fallback, and the way to see what it buys.
   */
  ibl: boolean;
  /**
   * Exposure multiplier, 0.7 to 1.6. Screens and eyes differ more than any
   * grade can allow for, and this game spends most of its time near black —
   * which is exactly where that difference is largest.
   */
  brightness: number;
  /**
   * Quality of each screen-space effect: 0 off, 4 ultra.
   *
   * Separate rather than one slider, because they cost differently and fail
   * differently. Reflections carry high-frequency detail and show their step
   * count immediately; occlusion and shadows are low-frequency and give up
   * very little for a lot of samples. Anyone tuning a frame wants to know
   * WHICH of the three bought the milliseconds.
   */
  gfxAo: number;
  gfxShadow: number;
  gfxSsr: number;
  /** Sky and cloud detail above the waterline. Costs nothing below it. */
  gfxSky: number;
  /**
   * Time of day, as a `SUN_PRESETS` id. Stored by id rather than by angle so a
   * later build can retune what "golden hour" means without every saved
   * setting still pointing at the old sun.
   */
  timeOfDay: string;
}

const SKEY = 'abyss.settings.v1';

export function loadSettings(): Settings {
  const def: Settings = {
    muted: false, volume: 0.85,
    musicVol: 0.8, sfxVol: 1, ambVol: 1,
    showKeymap: true, frameCap: 60, tier: 'auto', hull: 'mk2', dof: 0, aa: 'taa',
    ssfx: true, ibl: true, brightness: 1, timeOfDay: DEFAULT_SUN, sv: 4,
    gfxAo: 3, gfxShadow: 3, gfxSsr: 2, gfxSky: 3, gfxAf: 8, gfxRes: 1,
  };
  const s = storage();
  if (!s) return def;
  try {
    // The file's OWN revision, read before the merge. Taken from `saved` it
    // would be the default's revision every time — `def` carries the current
    // one, so it masks the absent field in exactly the old files the migration
    // below exists for, and every migration silently becomes a no-op.
    const raw = JSON.parse(s.getItem(SKEY) ?? '{}') as Partial<Settings>;
    const rev = raw.sv ?? 1;
    const saved = { ...def, ...raw } as Settings & { dof: unknown };
    // `dof` used to be a switch and is now an aperture. Anyone who turned the
    // old one on chose the look it had, which was f/2.8 — so give them that
    // rather than a number they never picked or a setting silently lost.
    if (typeof saved.dof === 'boolean') saved.dof = saved.dof ? 2.8 : 0;
    if (typeof saved.dof !== 'number' || !Number.isFinite(saved.dof)) saved.dof = 0;
    // TAA IS THE DEFAULT NOW, AND A DEFAULT NOBODY RECEIVES IS NOT A DEFAULT.
    //
    // Settings are merged over `def`, so anyone with a file already written
    // keeps whatever it holds — and every one of those files holds 'auto',
    // because that is what the default was. Changing `def` alone would ship
    // the new value to new players and to nobody else.
    //
    // Rewriting 'auto' is safe in a way that rewriting any other value would
    // not be: 'auto' IS "I did not choose". Anyone who picked MSAA, or turned
    // antialiasing off deliberately, is left alone.
    //
    // Behind `sv` because it has to happen ONCE. Unconditionally it would fire
    // on every load, and AUTO would quietly stop being a thing anyone could
    // select — pick it, and it is TAA again next launch. The revision rides on
    // the same object the game writes back, so the first setting anyone
    // changes retires this for good.
    if (rev < 2) {
      if (saved.aa === 'auto') saved.aa = 'taa';
      saved.sv = 2;
    }
    // REFLECTIONS DEFAULTED TO OFF, AND THE THING THEY WERE OFF FOR NOW EXISTS.
    //
    // The default was 0 when the world was terrain, kelp and fish — diffuse
    // surfaces with nothing worth mirroring, so the pass cost milliseconds to
    // change nothing. The habitat kit is the first hard-surfaced, metallic,
    // glazed thing in the game, and without this pass its chrome reflects the
    // studio softbox and otherwise nothing at all: a mirror with no image in
    // it, which reads as flat grey paint.
    //
    // Same shape as the `aa` migration above and the same justification: 0 was
    // the DEFAULT, so a file holding it is overwhelmingly "I never chose",
    // and `sv` retires the rewrite after one load so OFF stays selectable.
    //
    // MEDIUM rather than ULTRA, and the difference is 7 marched steps against
    // 10. Nothing here is tier-aware — these four ride at the same level on a
    // phone as on the desktop that gets tuned — so the default has to be the
    // value that is defensible on the worst device that will load it, and
    // anyone wanting the other three steps can say so in OPTIONS.
    //
    // `gfxAf` needs no migration and deliberately has none — a field absent
    // from every existing file already takes the default through the merge.
    // Migrations are for defaults that CHANGED, not for settings that are new.
    if (rev < 3) {
      if (saved.gfxSsr === 0) saved.gfxSsr = 2;
      saved.sv = 3;
    }
    // A file written by a newer build, or a hand-edited one. The sampler takes
    // this straight to the GPU, so it is clamped to the set WebGPU accepts
    // rather than trusted.
    const AF_OK = [1, 2, 4, 8, 16];
    if (!AF_OK.includes(saved.gfxAf)) saved.gfxAf = 8;
    const RES_OK = [1, 1.5, 2, 3, 0];
    if (!RES_OK.includes(saved.gfxRes)) saved.gfxRes = 1;
    return saved as Settings;
  } catch {
    return def;
  }
}

export function writeSettings(v: Settings) {
  try {
    storage()?.setItem(SKEY, JSON.stringify(v));
  } catch { /* ignore */ }
}

/**
 * The aperture ring, widest first.
 *
 * Real stops rather than a percentage, because the effect they name is one
 * photographers already have an intuition for: small number, shallow focus and
 * a soft distance; large number, everything holds together.
 */
export const F_STOPS = [1.2, 1.4, 1.8, 2.2, 2.8, 4, 8, 12];
